import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { API } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { EarthScreen, SpinningEarth } from "@/components/Earth";

const ping = () => axios.get(`${API}/meta`, { timeout: 8000 }).then(() => true).catch(() => false);

const COPY = {
  offline: ["You're offline", "Please give the Earth some time to regain its strength. We'll reconnect as soon as your internet is back."],
  down: ["Our servers are taking a breather", "Like a tired tree after a hot Jaipur afternoon. Please try again in a moment."],
};

export const NetStatus = () => {
  const { t } = useApp();
  const [state, setState] = useState(navigator.onLine ? null : "offline");
  const [slow, setSlow] = useState(false);
  const [busy, setBusy] = useState(false);
  const back = useCallback(() => { setState((s) => { if (s) toast.success(t("Back online. The Earth thanks you for your patience!")); return null; }); }, [t]);

  useEffect(() => {
    const off = () => setState("offline");
    const on = () => ping().then((ok) => (ok ? back() : setState("down")));
    const net = (e) => {
      if (e.detail === "slow") setSlow(true);
      if (e.detail === "fast") setSlow(false);
      if (e.detail === "down") ping().then((ok) => !ok && setState(navigator.onLine ? "down" : "offline"));
    };
    window.addEventListener("offline", off);
    window.addEventListener("online", on);
    window.addEventListener("ks-net", net);
    return () => { window.removeEventListener("offline", off); window.removeEventListener("online", on); window.removeEventListener("ks-net", net); };
  }, [back]);

  const retry = async () => { setBusy(true); const ok = navigator.onLine && (await ping()); setBusy(false); ok ? back() : toast.error(t("Still no connection. The Earth needs a little more time.")); };

  if (state) return (
    <EarthScreen overlay badge={state} testid={`net-${state}`} title={COPY[state][0]} sub={COPY[state][1]}>
      <div className="flex w-full max-w-xs flex-col gap-2">
        <button onClick={retry} disabled={busy} data-testid="net-retry-btn" className="flex h-12 items-center justify-center gap-2 rounded-2xl bg-[#0D3B2B] font-bold text-white active:scale-95 transition-transform disabled:opacity-60">
          <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />{t("Try again")}</button>
        <button onClick={() => setState(null)} data-testid="net-dismiss-btn" className="h-11 text-sm font-semibold text-[#2E9E6B]">{t("Continue browsing saved pages")}</button>
      </div>
    </EarthScreen>
  );
  if (!slow) return null;
  return (
    <div data-testid="net-slow" className="ks-rise fixed bottom-24 left-1/2 z-[1500] flex -translate-x-1/2 items-center gap-2 rounded-full bg-[#0D3B2B] py-1.5 pl-1.5 pr-4 text-xs font-semibold text-white shadow-lg">
      <SpinningEarth size={28} />{t("Slow connection… planting a few seeds while we wait")}
    </div>
  );
};
